import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Check } from 'lucide-react';
import WebCta from './WebCta';
import { normalizePhone } from '../services/firebase';
import { WhoHadThisParticipant } from './WebWhoHadThisSheet';
import { resolveWebParticipantIdentity } from '../utils/webParticipantAvatar';
import { useSheetLifecycle } from '../hooks/useSheetLifecycle';
import { triggerUiHaptic } from '../utils/webHaptics';

export type WebCoverSomeoneSheetProps = {
    visible: boolean;
    onClose: () => void;
    userPhone: string;
    organizerPhone?: string;
    organizerName?: string;
    organizerPhotoURL?: string | null;
    session?: any;
    participants: WhoHadThisParticipant[];
    currentCoveredPhones?: string[];
    onSelectCoveredPersons: (coveredParticipants: WhoHadThisParticipant[]) => void;
};

export const WebCoverSomeoneSheet: React.FC<WebCoverSomeoneSheetProps> = ({
    visible,
    onClose,
    userPhone,
    organizerPhone,
    organizerName,
    organizerPhotoURL,
    session,
    participants,
    currentCoveredPhones = [],
    onSelectCoveredPersons,
}) => {
    const { isRendered, isClosing, handleClose: handleSafeClose } = useSheetLifecycle(visible, onClose, 220);

    const normViewer = normalizePhone(userPhone || '');
    const normOrg = normalizePhone(organizerPhone || '');

    const eligibleParticipants = useMemo(() => {
        return participants.filter((p) => {
            const normP = normalizePhone(p.phone || p.id) || p.phone || p.id;
            const isPaid = Boolean(p.isPaid || p.isPaymentLocked || p.isLocked);
            const isDeclined = Boolean(p.isDeclined);
            const isOrganizer = Boolean(p.isOrganizer || (normOrg && normP === normOrg));
            return normP !== normViewer && !isPaid && !isDeclined && !isOrganizer;
        });
    }, [participants, normViewer, normOrg]);

    const [selectedPhoneSet, setSelectedPhoneSet] = useState<Set<string>>(() => {
        return new Set(currentCoveredPhones.map((ph) => normalizePhone(ph) || ph));
    });

    const wasVisibleRef = useRef(false);
    useEffect(() => {
        if (visible && !wasVisibleRef.current) {
            setSelectedPhoneSet(new Set(currentCoveredPhones.map((ph) => normalizePhone(ph) || ph)));
        }
        wasVisibleRef.current = visible;
    }, [visible, currentCoveredPhones]);

    const selectedParticipants = useMemo(() => {
        return eligibleParticipants.filter((p) => {
            const normP = normalizePhone(p.phone || p.id) || p.phone || p.id;
            return selectedPhoneSet.has(normP);
        });
    }, [eligibleParticipants, selectedPhoneSet]);

    const allSelected = eligibleParticipants.length > 0 && selectedParticipants.length === eligibleParticipants.length;

    const handleToggle = (phone: string) => {
        triggerUiHaptic('selection');
        setSelectedPhoneSet((prev) => {
            const next = new Set(prev);
            if (next.has(phone)) {
                next.delete(phone);
            } else {
                next.add(phone);
            }
            return next;
        });
    };

    const handleToggleSelectAll = () => {
        triggerUiHaptic('selection');
        if (allSelected) {
            setSelectedPhoneSet(new Set());
        } else {
            const next = new Set<string>();
            eligibleParticipants.forEach((p) => {
                const normP = normalizePhone(p.phone || p.id) || p.phone || p.id;
                next.add(normP);
            });
            setSelectedPhoneSet(next);
        }
    };

    const handleSave = () => {
        triggerUiHaptic('impactMedium');
        onSelectCoveredPersons(selectedParticipants);
        handleSafeClose();
    };

    const ctaTitle = useMemo(() => {
        if (selectedParticipants.length === 0) return 'Save coverage';
        if (selectedParticipants.length === 1) {
            const resolved = resolveWebParticipantIdentity({
                participant: selectedParticipants[0],
                viewerPhone: userPhone,
                organizerPhone,
                organizerName,
                organizerPhotoURL,
                session,
            });
            return `Cover ${resolved.canonicalName || selectedParticipants[0].name || 'Friend'}`;
        }
        return `Cover ${selectedParticipants.length} people`;
    }, [selectedParticipants, userPhone, organizerPhone, organizerName, organizerPhotoURL, session]);

    if (!isRendered) return null;

    return (
        <div className={`fixed inset-0 z-[80] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 overscroll-contain ${
            isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
        }`}>
            <div className="absolute inset-0" onClick={handleSafeClose} />
            <div
                className={`relative w-full max-w-lg bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] flex flex-col max-h-[90vh] overflow-hidden overscroll-contain ${
                    isClosing ? 'animate-sheet-exit' : 'animate-sheet-enter'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag Handle */}
                <div className="pt-3 pb-1 flex justify-center">
                    <div className="w-9 h-1 bg-slate-300 rounded-full" />
                </div>

                {/* Header */}
                <div className="px-6 pt-2 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-extrabold text-slate-900 leading-tight">
                            Paying for someone else?
                        </h3>
                        <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                            Select who you want to cover for this bill.
                        </p>
                    </div>
                    {eligibleParticipants.length > 1 ? (
                        <button
                            type="button"
                            onClick={handleToggleSelectAll}
                            className="shrink-0 bg-[#EEF0F4] hover:bg-slate-200 text-[#475569] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors active:scale-95"
                        >
                            {allSelected ? 'Clear all' : 'Select all'}
                        </button>
                    ) : null}
                </div>

                {/* Participant Checklist */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-2">
                    {eligibleParticipants.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 font-semibold text-sm">
                            No other eligible friends to cover.
                        </div>
                    ) : (
                        eligibleParticipants.map((participant) => {
                            const normP = normalizePhone(participant.phone || participant.id) || participant.phone || participant.id;
                            const isSelected = selectedPhoneSet.has(normP);
                            const resolved = resolveWebParticipantIdentity({
                                participant,
                                viewerPhone: userPhone,
                                organizerPhone,
                                organizerName,
                                organizerPhotoURL,
                                session,
                            });

                            return (
                                <div
                                    key={participant.id || normP}
                                    onClick={() => handleToggle(normP)}
                                    className={`flex items-center justify-between p-3.5 rounded-2xl transition-all cursor-pointer active:scale-[0.99] ${
                                        isSelected
                                            ? 'bg-[#E6F4F1] text-slate-900'
                                            : 'bg-white hover:bg-slate-50 text-slate-900'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0 pr-3">
                                        <div className="w-10 h-10 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center overflow-hidden shrink-0 border border-white">
                                            {resolved.photoURL ? (
                                                <img src={resolved.photoURL} alt={resolved.canonicalName || participant.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <span>{resolved.initials}</span>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-sm text-slate-900 truncate">
                                                {resolved.canonicalName || participant.name}
                                            </p>
                                            <p className="text-xs text-slate-500 font-medium">
                                                {isSelected ? 'Covering their share' : 'Not covered'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                        isSelected
                                            ? 'bg-[#0D9488] border-[#0D9488] text-white'
                                            : 'border-slate-300 bg-white'
                                    }`}>
                                        {isSelected ? <Check className="w-3.5 h-3.5 stroke-[3]" /> : null}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Save CTA */}
                <div className="p-6 border-t border-slate-100 bg-white">
                    <WebCta
                        title={ctaTitle}
                        variant="contrast"
                        onClick={handleSave}
                    />
                </div>
            </div>
        </div>
    );
};

export default WebCoverSomeoneSheet;
